import { useEffect, useRef, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { AcceptTermsResponseDto } from '@splitfiesta/shared';
import { Card, FormButton, FormCheckbox, Text } from '@splitfiesta/ui';

import { LegalText } from '@/components/auth/LegalText';

import { useRouter } from '@/hooks/useRouteChangeRouter';
import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';

/**
 * Post-OAuth ToS/Privacy acceptance gate (store compliance — Apple 1.2 /
 * Play UGC policy). Mounted once in App: whenever the signed-in user has
 * no `termsAcceptedAt`, a blocking overlay collects an active agreement
 * before the app can be used.
 *
 * Email signups tick the checkbox on the register form before an account
 * exists; RegisterPage stamps sessionStorage, and this gate consumes the
 * stamp to record the acceptance silently — those users never see the
 * overlay.
 */

export const PRESIGNUP_TERMS_KEY = 'sf-presignup-terms-accepted';

export function TermsAcceptanceGate() {
  const user = useAuthStore(state => state.user);
  const updateUser = useAuthStore(state => state.updateUser);
  const api = useTypedApi();
  const router = useRouter();
  const { t } = useTranslation();
  const authLoaded = useNamespace('auth');
  const errorsLoaded = useNamespace('errors');

  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [verified, setVerified] = useState(false);
  const silentAttempted = useRef(false);
  const verifyAttempted = useRef(false);

  const needsAcceptance = Boolean(user && !user.termsAcceptedAt);

  const recordAcceptance = async () => {
    const response = await api.user.acceptTerms();
    const data = response.data as AcceptTermsResponseDto;
    updateUser({ termsAcceptedAt: data.termsAcceptedAt });
  };

  // Consume the register-form stamp: acceptance was already given on the
  // signup form, so record it without showing the overlay.
  useEffect(() => {
    if (!needsAcceptance || silentAttempted.current) return;
    if (sessionStorage.getItem(PRESIGNUP_TERMS_KEY) !== 'true') return;
    silentAttempted.current = true;
    recordAcceptance()
      .then(() => sessionStorage.removeItem(PRESIGNUP_TERMS_KEY))
      .catch(() => {
        // Fall through to the visible overlay on the next render.
        silentAttempted.current = false;
        sessionStorage.removeItem(PRESIGNUP_TERMS_KEY);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAcceptance]);

  // Self-heal a stale persisted store: the local user only refreshes on
  // interactive logins, so a browser whose stored user predates an
  // acceptance made elsewhere would re-ask. Confirm with the server
  // before ever showing the overlay; if the account already accepted,
  // patch the store and stay invisible.
  useEffect(() => {
    if (!needsAcceptance || verifyAttempted.current) return;
    verifyAttempted.current = true;
    api.auth
      .me()
      .then(response => {
        const me = response.data as { termsAcceptedAt?: string };
        if (me.termsAcceptedAt) {
          updateUser({ termsAcceptedAt: me.termsAcceptedAt });
        } else {
          setVerified(true);
        }
      })
      .catch(() => {
        // Offline / server hiccup — fall through to asking. Accepting
        // again is harmless (the backend keeps the first timestamp).
        setVerified(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAcceptance]);

  if (
    !needsAcceptance ||
    !verified ||
    !authLoaded ||
    !errorsLoaded ||
    sessionStorage.getItem(PRESIGNUP_TERMS_KEY) === 'true'
  ) {
    return null;
  }

  const handleContinue = async () => {
    setError(false);
    setSubmitting(true);
    try {
      await recordAcceptance();
    } catch {
      setError(true);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="terms-acceptance-gate"
    >
      <Card className="w-full max-w-md">
        <Text className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('auth:legal.gateTitle')}
        </Text>
        <Text
          variant="bodySmall"
          className="mt-1 mb-4 text-gray-500 dark:text-gray-400"
        >
          {t('auth:legal.gateSubtitle')}
        </Text>

        {error && (
          <Text variant="bodySmall" className="mb-3 text-red-600">
            {t('errors:system.internal')}
          </Text>
        )}

        <FormCheckbox
          id="gate-terms-accepted"
          label={<LegalText text={t('auth:legal.termsAgreement')} />}
          checked={accepted}
          onChange={setAccepted}
          disabled={submitting}
          className="mb-4"
          labelClassName="font-normal"
          data-testid="gate-terms-checkbox"
        />

        <FormButton
          type="button"
          onClick={handleContinue}
          disabled={!accepted}
          loading={submitting}
          fullWidth
          data-testid="gate-continue-button"
        >
          {t('auth:legal.gateContinue')}
        </FormButton>

        <FormButton
          type="button"
          variant="outline"
          onClick={() => router.push('/logout')}
          disabled={submitting}
          fullWidth
          className="mt-2"
          data-testid="gate-signout-button"
        >
          {t('auth:legal.gateSignOut')}
        </FormButton>
      </Card>
    </div>
  );
}
